import { useEffect, useRef, useState } from "react";
import { business } from "@/lib/business-data";
import BrandLogo from "@/components/BrandLogo";

const MIN_MS = 900;
const MAX_MS = 2200;
const EXIT_MS = 620;

interface Props {
  onDone?: () => void;
}

/**
 * Brand loader: the logo mark inside a sweeping brand-gradient ring, over a
 * deep navy field, with a progress rail that tracks real load progress and a
 * curtain exit. Respects prefers-reduced-motion.
 */
const PremiumLoader = ({ onDone }: Props) => {
  const [progress, setProgress] = useState(8);
  const [hiding, setHiding] = useState(false);
  const [removed, setRemoved] = useState(false);
  const doneRef = useRef(false);

  useEffect(() => {
    const start = performance.now();
    const timers: ReturnType<typeof setTimeout>[] = [];
    let raf = 0;

    // Ease the rail toward 90% while the page is still loading.
    const tick = () => {
      setProgress((p) => (p < 90 ? p + (90 - p) * 0.035 + 0.25 : p));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const finish = () => {
      if (doneRef.current) return;
      doneRef.current = true;
      cancelAnimationFrame(raf);
      setProgress(100);

      const wait = Math.max(MIN_MS - (performance.now() - start), 0);
      timers.push(
        setTimeout(() => {
          setHiding(true);
          timers.push(
            setTimeout(() => {
              setRemoved(true);
              onDone?.();
            }, EXIT_MS)
          );
        }, wait)
      );
    };

    timers.push(setTimeout(finish, MAX_MS));
    if (document.readyState === "complete") finish();
    else window.addEventListener("load", finish, { once: true });

    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      window.removeEventListener("load", finish);
    };
  }, [onDone]);

  if (removed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Loading"
      aria-hidden={hiding}
      className={`fixed inset-0 z-[10000] flex items-center justify-center overflow-hidden ${
        hiding ? "pointer-events-none" : ""
      }`}
      style={{
        background:
          "radial-gradient(120% 90% at 50% 30%, hsl(var(--accent)) 0%, hsl(var(--accent-dark)) 70%)",
        opacity: hiding ? 0 : 1,
        transform: hiding ? "scale(1.04)" : "scale(1)",
        transition: `opacity ${EXIT_MS}ms cubic-bezier(0.65,0,0.35,1), transform ${EXIT_MS}ms cubic-bezier(0.65,0,0.35,1)`,
      }}
    >
      {/* Ambient brand glows */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -left-32 h-[36rem] w-[36rem] rounded-full blur-3xl opacity-40 loader-drift"
        style={{ background: "radial-gradient(circle, hsl(var(--primary) / 0.55), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-48 -right-32 h-[32rem] w-[32rem] rounded-full blur-3xl opacity-30 loader-drift-slow"
        style={{ background: "radial-gradient(circle, hsl(var(--secondary) / 0.5), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(hsl(0 0% 100% / 0.6) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 0.6) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "radial-gradient(ellipse at center, black 20%, transparent 72%)",
          WebkitMaskImage: "radial-gradient(ellipse at center, black 20%, transparent 72%)",
        }}
      />

      <div
        className="relative flex flex-col items-center px-6"
        style={{
          opacity: hiding ? 0 : 1,
          transform: hiding ? "translateY(-14px)" : "translateY(0)",
          transition: "opacity 380ms ease-out, transform 480ms cubic-bezier(0.33,1,0.68,1)",
        }}
      >
        {/* Logo mark inside a sweeping brand-gradient ring */}
        <div className="relative h-32 w-32 sm:h-36 sm:w-36">
          <div
            aria-hidden="true"
            className="absolute inset-0 rounded-full loader-ring"
            style={{
              background:
                "conic-gradient(from 0deg, transparent 0deg, hsl(var(--primary-on-dark)) 90deg, hsl(var(--secondary-on-dark)) 200deg, transparent 300deg)",
              WebkitMask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
              mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
            }}
          />
          <div
            aria-hidden="true"
            className="absolute inset-[3px] rounded-full"
            style={{ border: "1px solid hsl(0 0% 100% / 0.1)" }}
          />
          <div className="absolute inset-[10px] rounded-full bg-white/95 shadow-[0_18px_50px_-12px_rgba(0,0,0,0.6)] flex items-center justify-center">
            <BrandLogo
              height={89}
              decorative
              className="h-[62%] w-[62%] object-contain loader-mark"
            />
          </div>
        </div>

        <p className="mt-8 font-heading text-2xl sm:text-3xl font-bold tracking-tight text-white text-center">
          {business.name}
        </p>
        <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.32em] text-white/55">
          {business.tagline}
        </p>

        {/* Progress rail */}
        <div className="relative mt-8 h-[3px] w-56 sm:w-64 overflow-hidden rounded-full bg-white/12">
          <div
            className="absolute inset-y-0 left-0 rounded-full"
            style={{
              width: `${progress}%`,
              background: "linear-gradient(90deg, hsl(var(--primary-on-dark)), hsl(var(--secondary-on-dark)))",
              boxShadow: "0 0 14px hsl(var(--primary-on-dark) / 0.7)",
              transition: "width 240ms cubic-bezier(0.33,1,0.68,1)",
            }}
          />
        </div>
        <p className="mt-4 text-[10px] font-medium uppercase tracking-[0.28em] text-white/45">
          {business.address.city}, {business.address.stateShort}
        </p>
      </div>

      <style>{`
        @keyframes loader-ring-spin { to { transform: rotate(360deg); } }
        @keyframes loader-mark-in {
          0%   { opacity: 0; transform: scale(0.82); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes loader-drift {
          0%, 100% { transform: translate3d(0, 0, 0); }
          50%      { transform: translate3d(28px, 24px, 0); }
        }
        .loader-ring { animation: loader-ring-spin 1.5s linear infinite; }
        .loader-mark { animation: loader-mark-in 700ms cubic-bezier(0.33, 1, 0.68, 1) both; }
        .loader-drift { animation: loader-drift 9s ease-in-out infinite; }
        .loader-drift-slow { animation: loader-drift 13s ease-in-out infinite reverse; }
        @media (prefers-reduced-motion: reduce) {
          .loader-ring, .loader-mark, .loader-drift, .loader-drift-slow {
            animation: none !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PremiumLoader;
